import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { client } from '../../client';
import type { Retailer } from '../../generated';
import type { CollectionScoringStatus } from '../get-collection';

/*
 * Hand-written stand-in for the pair filter on the collection images listing. The API already
 * accepts `product_category_id` plus one of `benchmark_id` or `retailer` on
 * GET /v1/workspaces/{workspace_id}/assets/images/collections/{collection_id}/images and answers
 * each image with its `pair_score` for that pair, but the spec the generator reads does not list
 * the parameters or the field yet. This seam asks for one image and returns only its pair score.
 * When the generated listing carries them, point this at `useSearchCollectionImagesQuery` and keep
 * the hook's name and shape; callers stay untouched. Tests answer this path with the MSW handler in
 * `tests/mocks/handlers.ts`.
 */

const COLLECTION_IMAGES_URL = '/v1/workspaces/{workspace_id}/assets/images/collections/{collection_id}/images';

/** An image's score for one (benchmark, product category) pair. */
export interface PairScoreView {
  benchmark_id: string;
  retailer: Retailer | null;
  product_category_id: string;
  model: 'clip' | 'effnet';
  raw_score: number | null;
  vizit_score: number | null;
  /** `false` while the pair is unscored, never requested, or its pool is empty. */
  scored: boolean;
  scoring_status: CollectionScoringStatus | null;
}

interface CollectionImageWithPairScore {
  id: string;
  pair_score?: PairScoreView | null;
}

export interface GetCollectionImagePairScoreQueryVariables {
  workspace_id: string;
  collection_id: string;
  image_id: string;
  product_category_id: string;
  /** Exactly one of `benchmark_id` (custom) or `retailer` (Vizit) names the benchmark. */
  benchmark_id?: string;
  retailer?: Retailer;
}

export type GetCollectionImagePairScoreQuery = PairScoreView | null;

async function getCollectionImagePairScore(variables: GetCollectionImagePairScoreQueryVariables) {
  const { workspace_id, collection_id, image_id, product_category_id, benchmark_id, retailer } = variables;
  const { data } = await client.get<{ 200: CollectionImageWithPairScore[] }, unknown, true>({
    url: COLLECTION_IMAGES_URL,
    path: { workspace_id, collection_id },
    query: { image_ids: [image_id], product_category_id, benchmark_id, retailer, limit: 1 },
    throwOnError: true,
  });
  return data.find((image) => image.id === image_id)?.pair_score ?? null;
}

export const useGetCollectionImagePairScoreQuery = <TData = GetCollectionImagePairScoreQuery, TError = unknown>(
  variables: GetCollectionImagePairScoreQueryVariables,
  options?: Omit<UseQueryOptions<GetCollectionImagePairScoreQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetCollectionImagePairScoreQuery, TError, TData>({
    queryKey: useGetCollectionImagePairScoreQuery.getKey(variables),
    queryFn: () => getCollectionImagePairScore(variables),
    ...options,
  });
};

useGetCollectionImagePairScoreQuery.getKey = (variables: GetCollectionImagePairScoreQueryVariables) => [
  'GetCollectionImagePairScore',
  variables,
];

useGetCollectionImagePairScoreQuery.fetcher = (variables: GetCollectionImagePairScoreQueryVariables) => () =>
  getCollectionImagePairScore(variables);
